import React, { useEffect, useContext, useCallback, useState } from 'react';
import { AssessmentContext } from '../../context/AssessmentContext.js';
import { WebSocketContext } from '../../context/WebSocketContext.js';
import { ReadyState } from 'react-use-websocket';
import { SAVE_ERROR, SAVE_SUCCESS } from './TestStatus.js';
import * as TestStatus from './TestStatus.js';
import * as Storage from '../common/Storage.js';
import useSessionStorage from '../../hooks/useSessionStorage';
import * as PwaEvents from '../../context/PwaEvents.js';
import ContinueDialog from './ContinueDialog.js';
import NetworkOrSaveErrorDialog from './NetworkOrSaveErrorDialog.js';
import CustomDialog from '../ui/feedback/CustomDialog.js';
import {
  ABORT_LOGIN_ERROR_DIALOG,
  DUPLICATE_LOGIN_ERROR_DIALOG,
  LAUNCH_ERROR_DIALOG,
  PROCTOR_PAUSE_DIALOG,
} from './LearnosityDialogs.js';
import TimedAutoExitDialog from './TimedAutoExitDialog.js';
import { NETWORK_ERROR_ACTION } from '../../scripts/network-error/networkError.js';
import SubmitErrorDialog from './SubmitErrorDialog.js';
import { checkReadyToResume } from '../../scripts/less-secure/enforceLessSecure.js';
import { constructMessage } from './WebsocketMessageHandler.js';
import { PSYCHOMETRIC_EVENTS } from './PyschometricEvents.js';
import { useTranslation } from 'react-i18next';

const AssessmentOverlays = (props) => {
  const { getStorageItem, setStorageItem, removeStorageItem } =
    useSessionStorage();
  const {
    testStatus,
    openLaunchErrorDialog,
    openContinueDialog,
    saveState,
    saveErrorCount,
    openErrorDialog,
    setOpenErrorDialog,
    setOpenContinueDialog,
    resetSaveErrorCount,
    updatePausedBy,
    openProctorPauseDialog,
    proctorPauseDialogProps,
    setExit,
    openExitingDialog,
    exitingDialogDetails,
    openSubmitDialog,
    setOpenSubmitDialog,
    setSubmitInProgress,
    learnosityRequestObj,
    loginData,
    setOpenProctorPauseDialog,
    setProctorPauseDialogProps,
  } = useContext(AssessmentContext);

  const { readyState, sendMsgToWebsocket } = useContext(WebSocketContext);
  const { t } = useTranslation();

  //Set the save and error state from session storage
  const __notifiedSaveErrorCount =
    getStorageItem(Storage.STORAGE_NOTIFIED_SAVE_ERROR_COUNT) || 0;
  const [notifiedSaveErrorCount, setNotifiedSaveErrorCount] = useState(
    __notifiedSaveErrorCount,
  );

  //Notified save error count
  const updateNotifiedSaveErrorCount = useCallback(() => {
    setNotifiedSaveErrorCount((prev) => {
      let newVal = prev + 1;
      setStorageItem(Storage.STORAGE_NOTIFIED_SAVE_ERROR_COUNT, newVal);
      return newVal;
    });
  }, [setStorageItem]);

  const resetNotifiedSaveErrorCount = useCallback(() => {
    setNotifiedSaveErrorCount(0);
    removeStorageItem(Storage.STORAGE_NOTIFIED_SAVE_ERROR_COUNT);
  }, [removeStorageItem]);

  useEffect(() => {
    //there is error between 0 - 2 and save state is success
    if (
      saveErrorCount > 0 &&
      saveErrorCount < TestStatus.MIN_CONSECUTIVE_ERRORS &&
      saveState === SAVE_SUCCESS
    ) {
      //reset save error counts
      resetSaveErrorCount();
      //reset notify error count
      resetNotifiedSaveErrorCount();
    }

    //there is error more than 3 and save state is success
    //if websocket is open send message and reset error count
    //There is case where we will only send reconnect and not disconnect
    if (
      saveErrorCount >= TestStatus.MIN_CONSECUTIVE_ERRORS &&
      saveState === SAVE_SUCCESS &&
      readyState === ReadyState.OPEN
      //&& notifiedSaveSuccessCount === 0
    ) {
      sendMsgToWebsocket(PwaEvents.CLIENT_LEARNOSITY_RECONNECT);

      //reset save error counts
      resetSaveErrorCount();
      //reset notify error count
      resetNotifiedSaveErrorCount();

      //handle dialogs
      setOpenErrorDialog(false);
      let endAction = getStorageItem(Storage.STORAGE_ENDED_ACTION);
      if (!endAction) {
        //Set submit In progress to undefined to make sure
        //Learnosity error save dialog does not show up in case the
        //user already submitted
        setSubmitInProgress();
        if (checkReadyToResume()) {
          sendMsgToWebsocket(
            constructMessage(
              PSYCHOMETRIC_EVENTS.READY_TO_RESUME,
              {
                user: PwaEvents.ACTOR_SYSTEM,
              },
              learnosityRequestObj,
              loginData,
            ),
            false,
          );
        }
        setOpenContinueDialog(true);
      }
    }

    //there is error 3 and above and save state is error
    if (
      saveErrorCount >= TestStatus.MIN_CONSECUTIVE_ERRORS &&
      saveState === SAVE_ERROR
    ) {
      //If socket is open send message
      if (readyState === ReadyState.OPEN && notifiedSaveErrorCount === 0) {
        sendMsgToWebsocket(PwaEvents.CLIENT_LEARNOSITY_DISCONNECT);
        updateNotifiedSaveErrorCount();
      }
      //handle error dialog
      updatePausedBy(TestStatus.PAUSED_BY_SYSTEM);
      setStorageItem(
        Storage.STORAGE_PAUSED_REASON,
        `${NETWORK_ERROR_ACTION.PREFIX}${NETWORK_ERROR_ACTION.LEARNOSITY.type}`,
      );
      setStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED, true);
      setOpenErrorDialog(true);
    }
  }, [
    notifiedSaveErrorCount,
    readyState,
    saveState,
    saveErrorCount,
    sendMsgToWebsocket,
    setOpenContinueDialog,
    setOpenErrorDialog,
    resetSaveErrorCount,
    updatePausedBy,
    updateNotifiedSaveErrorCount,
    resetNotifiedSaveErrorCount,
    getStorageItem,
    setStorageItem,
    setSubmitInProgress,
    learnosityRequestObj,
    loginData,
  ]);

  const onComplete = useCallback(() => {
    setExit(true);
  }, [setExit]);

  const TIMEDEXIT_ERROR_ACTIONS = [
    {
      label: t('common.ok'),
      handle: onComplete,
    },
  ];

  const TIMEDEXIT_END_ACTIONS = [
    {
      label: t('common.exit'),
      handle: onComplete,
    },
  ];

  //This useeffect will only be run on first load or reload. 
  //we get status from storage to remove dependency on states.
  //This will handle refresh - pause on awaitingLaunch
  useEffect(() => {
    let testStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);
    let statusBeforePause = getStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE);
    if (
      testStatus === TestStatus.TEST_PAUSED &&
      statusBeforePause === TestStatus.TEST_AWAITING_LAUNCH
    ) {
      setProctorPauseDialogProps(PROCTOR_PAUSE_DIALOG());
      setOpenProctorPauseDialog(true);
    }
  }, [getStorageItem, setProctorPauseDialogProps, setOpenProctorPauseDialog]);

  return (
    <>
      <TimedAutoExitDialog
        open={openLaunchErrorDialog}
        dialogDetails={LAUNCH_ERROR_DIALOG()}
        onComplete={onComplete}
        actions={TIMEDEXIT_ERROR_ACTIONS}
        duration={30}
        storageKey={Storage.STORAGE_LAUNCH_AUTO_EXIT_SECONDS}
      />
      {testStatus === TestStatus.TEST_DUPLICATE && (
        <TimedAutoExitDialog
          open={openErrorDialog}
          dialogDetails={DUPLICATE_LOGIN_ERROR_DIALOG()}
          onComplete={onComplete}
          actions={TIMEDEXIT_ERROR_ACTIONS}
          duration={30}
        />
      )}
      {testStatus === TestStatus.TEST_ABORT && (
        <TimedAutoExitDialog
          open={openErrorDialog}
          dialogDetails={ABORT_LOGIN_ERROR_DIALOG()}
          onComplete={onComplete}
          actions={TIMEDEXIT_ERROR_ACTIONS}
          duration={10}
        />
      )}
      {openSubmitDialog && (
        <SubmitErrorDialog
          open={openSubmitDialog}
          setOpen={setOpenSubmitDialog}
        />
      )}
      {openProctorPauseDialog && (
        <CustomDialog
          open={openProctorPauseDialog}
          dialogProps={{
            title: proctorPauseDialogProps?.header
              ? proctorPauseDialogProps?.header
              : PROCTOR_PAUSE_DIALOG().header,
            message: proctorPauseDialogProps?.body
              ? proctorPauseDialogProps?.body
              : PROCTOR_PAUSE_DIALOG().body,
            actions: proctorPauseDialogProps?.buttons,
          }}
        />
      )}
      {openExitingDialog && (
        <TimedAutoExitDialog
          open={openExitingDialog}
          dialogDetails={exitingDialogDetails}
          onComplete={onComplete}
          actions={TIMEDEXIT_END_ACTIONS}
          duration={10}
        />
      )}
      {![TestStatus.TEST_DUPLICATE, TestStatus.TEST_ABORT].includes(
        testStatus,
      ) &&
        openErrorDialog && <NetworkOrSaveErrorDialog open={openErrorDialog} />}
      <ContinueDialog
        open={openContinueDialog}
        setOpen={setOpenContinueDialog}
      />
    </>
  );
};

export default AssessmentOverlays;
